import React, { useState, useCallback } from 'react';

const API_BASE_URL = '';

interface AuditData {
  user: any;
  sponsor: any;
  downlineCount: number;
  correctCalculation: {
    directPurchaseShares: number;
    directPurchaseAmount: number;
    directPurchaseCount: number;
    commissionLevelShares: number;
    commissionLevelAmount: number;
    commissionLevelCount: number;
    usdtConversionShares: number;
    usdtConversionAmount: number;
    usdtConversionCount: number;
    bonusShares: number;
    presaleCommissionShares: number;
    presaleCommissionCount: number;
    conversionShares: number;
    conversionCount: number;
    legacyShares: number;
    transfersSent: number;
    transfersReceived: number;
    netTransfers: number;
    correctTotal: number;
    wholeShares: number;
  };
  comparison: {
    mlcSharesTotal: number;
    mlcRecordCount: number;
    commBalanceShareBalance: number;
    commBalanceTotalEarned: number;
    commBalanceUsdtBalance: number;
    commBalanceTotalEarnedUsdt: number;
    commBalanceTotalWithdrawn: number;
  };
  records: any;
  discrepancies: string[];
}

// === Sub-components ===
const Section: React.FC<{ title: string; highlight?: boolean; children: React.ReactNode }> = ({ title, highlight, children }) => (
  <div style={{ background: highlight ? '#1a2332' : '#1f2937', border: `1px solid ${highlight ? '#f59e0b44' : '#374151'}`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <h3 style={{ margin: '0 0 12px 0', fontSize: 16, color: highlight ? '#f59e0b' : '#9ca3af' }}>{title}</h3>
    {children}
  </div>
);

const Row: React.FC<{ label: string; value: string; warn?: boolean }> = ({ label, value, warn }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', borderBottom: '1px solid #374151' }}>
    <span style={{ color: '#ffffff', fontSize: 13 }}>{label}</span>
    <span style={{ color: warn ? '#ef4444' : '#e5e7eb', fontSize: 13, fontWeight: warn ? 'bold' : 'normal' }}>{value}{warn ? ' ⚠️' : ''}</span>
  </div>
);

const BreakdownRow: React.FC<{ label: string; shares: number; amount?: number; count?: number; subtitle?: string; bold?: boolean; onClick?: () => void }> = ({ label, shares, amount, count, subtitle, bold, onClick }) => (
  <div onClick={onClick} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #374151', cursor: onClick ? 'pointer' : 'default', background: onClick ? '#1f293755' : 'transparent' }}>
    <div>
      <span style={{ color: bold ? '#f59e0b' : '#e5e7eb', fontSize: bold ? 15 : 13, fontWeight: bold ? 'bold' : 'normal' }}>{label}</span>
      {count !== undefined && <span style={{ color: '#ffffff', fontSize: 11, marginLeft: 8 }}>({count} records)</span>}
      {subtitle && <div style={{ color: '#ffffff', fontSize: 11 }}>{subtitle}</div>}
      {onClick && <span style={{ color: '#ffffff', fontSize: 11, marginLeft: 8 }}>▶ click to expand</span>}
    </div>
    <div style={{ textAlign: 'right' }}>
      <span style={{ color: bold ? '#f59e0b' : shares === 0 ? '#6b7280' : '#10b981', fontSize: bold ? 15 : 13, fontWeight: bold ? 'bold' : 'normal' }}>{shares.toFixed(6)}</span>
      {amount !== undefined && amount > 0 && <div style={{ color: '#ffffff', fontSize: 11 }}>${amount.toFixed(2)}</div>}
    </div>
  </div>
);

const RecordTable: React.FC<{ title: string; records: any[] }> = ({ title, records }) => {
  if (!records || records.length === 0) return <div style={{ padding: 12, color: '#ffffff', fontSize: 13 }}>No {title.toLowerCase()} found.</div>;
  const keys = Object.keys(records[0]).filter(k => k !== 'id');
  return (
    <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: 12, marginBottom: 16, overflowX: 'auto' }}>
      <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#ffffff' }}>{title} ({records.length})</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr>{keys.map(k => <th key={k} style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #4b5563', color: '#ffffff' }}>{k}</th>)}</tr>
        </thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #374151' }}>
              {keys.map(k => <td key={k} style={{ padding: '4px 6px', color: '#ffffff', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(r[k] ?? '')}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface ShareAuditPageProps {
  onBack: () => void;
  currentUserId: number;
}

const ShareAuditPage: React.FC<ShareAuditPageProps> = ({ onBack, currentUserId }) => {
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [auditData, setAuditData] = useState<AuditData | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const fetchAudit = useCallback(async () => {
    if (!searchInput.trim()) return;
    setLoading(true);
    setError('');
    setAuditData(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/share-audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestingUserId: currentUserId,
          targetUserId: searchInput.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch audit data');
      setAuditData(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [searchInput, currentUserId]);

  const fmt = (n: number, decimals = 6) => n.toFixed(decimals);
  const fmtUsd = (n: number) => `$${n.toFixed(2)}`;

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (currentUserId !== 106) {
    return <div style={{ padding: 40, color: '#ef4444', textAlign: 'center' }}>Access denied.</div>;
  }

  const c = auditData?.correctCalculation;
  const cmp = auditData?.comparison;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px', fontFamily: 'monospace', color: '#e5e7eb', background: '#111827', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: '#374151', color: '#e5e7eb', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}>← Back</button>
        <h1 style={{ margin: 0, fontSize: 24, color: '#f59e0b' }}>🔍 Share Audit Tool</h1>
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchAudit()}
          placeholder="Enter User ID or Username (e.g. 1747 or john_doe)"
          style={{ flex: 1, padding: '10px 14px', background: '#1f2937', border: '1px solid #374151', borderRadius: 6, color: '#e5e7eb', fontSize: 16 }}
        />
        <button onClick={fetchAudit} disabled={loading} style={{ padding: '10px 24px', background: '#f59e0b', color: '#111827', border: 'none', borderRadius: 6, fontWeight: 'bold', cursor: 'pointer', opacity: loading ? 0.5 : 1 }}>
          {loading ? 'Loading...' : 'Audit'}
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#7f1d1d', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

      {auditData && c && cmp && (
        <>
          {/* User Info */}
          <Section title={`👤 ${auditData.user?.full_name || auditData.user?.username} (ID: ${auditData.user?.id})`}>
            <Row label="Username" value={auditData.user?.username} />
            <Row label="Email" value={auditData.user?.email} />
            <Row label="Registered" value={new Date(auditData.user?.created_at).toLocaleDateString()} />
            <Row label="Sponsor" value={auditData.sponsor ? `${auditData.sponsor.sponsorUsername} (ID: ${auditData.sponsor.referrer_id})` : '⚠️ NO SPONSOR'} />
            <Row label="Downline" value={`${auditData.downlineCount} referrals`} />
          </Section>

          {/* CORRECT Share Breakdown */}
          <Section title="✅ CORRECT Share Calculation" highlight>
            <BreakdownRow label="Direct Purchases" shares={c.directPurchaseShares} amount={c.directPurchaseAmount} count={c.directPurchaseCount} onClick={() => toggleSection('direct')} />
            <BreakdownRow label="Commission Level Shares" shares={c.commissionLevelShares} amount={c.commissionLevelAmount} count={c.commissionLevelCount} onClick={() => toggleSection('commission')} />
            <BreakdownRow label="USDT Conversion Shares" shares={c.usdtConversionShares} amount={c.usdtConversionAmount} count={c.usdtConversionCount} onClick={() => toggleSection('usdtconv')} />
            <BreakdownRow label="Bonus Shares" shares={c.bonusShares} />
            <BreakdownRow label="Presale Commission" shares={c.presaleCommissionShares} count={c.presaleCommissionCount} onClick={() => toggleSection('presale')} />
            <BreakdownRow label="Conversion Shares" shares={c.conversionShares} count={c.conversionCount} onClick={() => toggleSection('conversions')} />
            <BreakdownRow label="Legacy Shares" shares={c.legacyShares} />
            <BreakdownRow label="Net Transfers" shares={c.netTransfers} subtitle={`Sent: ${fmt(c.transfersSent)} | Received: ${fmt(c.transfersReceived)}`} onClick={() => toggleSection('transfers')} />
            <div style={{ borderTop: '2px solid #f59e0b', marginTop: 8, paddingTop: 8 }}>
              <BreakdownRow label="✅ CORRECT TOTAL" shares={c.correctTotal} bold />
              <div style={{ color: '#ffffff', fontSize: 13, paddingLeft: 8 }}>Whole shares: {c.wholeShares}</div>
            </div>
          </Section>

          {/* Comparison / Discrepancy */}
          <Section title="⚠️ Comparison (NOT used in total — audit only)">
            <Row label="multi_level_commissions.shares_purchased" value={fmt(cmp.mlcSharesTotal)} warn={Math.abs(c.commissionLevelShares - cmp.mlcSharesTotal) > 0.01} />
            <Row label="commission_balances.share_balance" value={fmt(cmp.commBalanceShareBalance)} warn={Math.abs(c.commissionLevelShares - cmp.commBalanceShareBalance) > 0.01} />
            <Row label="commission_balances.total_earned_shares" value={fmt(cmp.commBalanceTotalEarned)} />
            <Row label="USDT Balance" value={fmtUsd(cmp.commBalanceUsdtBalance)} />
            <Row label="Total Earned USDT" value={fmtUsd(cmp.commBalanceTotalEarnedUsdt)} />
            <Row label="Total Withdrawn" value={fmtUsd(cmp.commBalanceTotalWithdrawn)} />
          </Section>

          {auditData.discrepancies.length > 0 && (
            <Section title="🚨 Discrepancies Found">
              {auditData.discrepancies.map((d, i) => (
                <div key={i} style={{ padding: '6px 8px', background: '#7f1d1d', borderRadius: 4, marginBottom: 4, fontSize: 13 }}>{d}</div>
              ))}
            </Section>
          )}

          {/* Expandable record tables */}
          {expandedSection === 'direct' && <RecordTable title="Direct Purchase Records" records={auditData.records.directPurchases} />}
          {expandedSection === 'commission' && <RecordTable title="Commission Level Records" records={auditData.records.commissionLevelPurchases} />}
          {expandedSection === 'usdtconv' && <RecordTable title="USDT Conversion Records" records={auditData.records.usdtConversions} />}
          {expandedSection === 'presale' && <RecordTable title="Presale Commission Records" records={auditData.records.presaleCommissions} />}
          {expandedSection === 'conversions' && <RecordTable title="Commission Conversion Records" records={auditData.records.conversionRecords} />}
          {expandedSection === 'transfers' && (
            <>
              <RecordTable title="Transfers Sent" records={auditData.records.transfersSentRecords} />
              <RecordTable title="Transfers Received" records={auditData.records.transfersReceivedRecords} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ShareAuditPage;

